import colors from '../styles/variables/exports/colors.scss?module'
import colorsForeground from '../styles/variables/exports/colors-foreground.scss?module'

export default {
  // Default export is a Vue plugin
  install (Vue) {
    // Read more about instance properties on https://vuejs.org/v2/cookbook/adding-instance-properties.html#Base-Example
    Vue.prototype['$color'] = {
      get all () {
        /**
         * Returns all usable colors and aliasses for them
         */
        return colors
      },
      getName (colorAlias) {
        /**
         * Returns color name when alias has been given
         * @color alias or color name 
         */
        return Object.keys(colors).find(colorName => {
          return colors[colorName]?.toString().toUpperCase() === colors[colorAlias]?.toString().toUpperCase()
        })
      },
      getHex (colorName) {
        /**
         * Returns hex value of the given color
         * @color alias or color name
         */
        let hexValue = colors[this.getName(colorName)]
        if (hexValue?.length <= 4) {
          // Hex is shortened (like #0FF), so duplicate (like #0FF0FF)
          hexValue += hexValue.substring(1,4)
        }
        return hexValue || null
      },
      getLuminance (color) {
        /**
         * Returns the WCAG2.0 relative luminance of a color
         * https://www.w3.org/TR/WCAG20/#relativeluminancedef
         * 0 = dark, 1 = white
         * @hex color
         */
        if (!color) { return null }
        const hex = color.includes('#') ? color : this.getHex(color)
        if (!hex) { return null }
        const rgb = [
          parseInt(hex?.replace('#', '').substring(0, 2), 16),
          parseInt(hex?.replace('#', '').substring(2, 4), 16),
          parseInt(hex?.replace('#', '').substring(4, 6), 16),
        ]
        const a = rgb.map(v => {
          v /= 255
          return (v <= 0.03928) ? (v / 12.92) : (Math.pow( (v + 0.055) / 1.055, 2.4 ))
        })
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
      },
      getForeground (color) {
        /**
         * Returns black or white based on contrast with given color (predefined, or calculated for WCAG2.0)
         * @color background color to calculate overlay color for
         */
        if (!color) return null

        // Return predefined foreground color
        const colorName = this.getName(color)
        if (colorName) {
          let hexWhite = new RegExp(/#(F+)/, "ig");
          let hexBlack = new RegExp(/#(0+)/, "ig");
          return colorsForeground[colorName]?.replace(hexWhite, 'white').replace(hexBlack, 'black')
        }
        
        // Return calculated foreground color
        const luminance = this.getLuminance(color)
        const luminanceBlack = 0
        const brightest = Math.max(luminance, luminanceBlack)
        const darkest = Math.min(luminance, luminanceBlack)
        if ((brightest + 0.05) / (darkest + 0.05) > 5) {
          return 'black'
        } else {
          return 'white'
        }
      },
      step (fromColor, steps) {
        /**
         * Returns color on the same scale, shifted by X steps
         * @fromColor color name (or alias) to shift from
         * @steps positive or negative integer
         */
        if (fromColor == null) return null
        if (steps == 0 || steps == null) return fromColor
  
        const fromColorName = this.getName(fromColor)
  
        // Get all colors in the scale of fromColor
        const colorScale = Object.keys(colors).filter(colorName => {
          return colorName?.replace(/[0-9]/g, '') === fromColorName?.replace(/[0-9]/g, '')
        })
        if (colorScale[0] !== 'white') {
          // Adds white to start of the scale
          colorScale.unshift('white')
        }
  
        // Get the position of the color on its scale
        const colorIndex = colorScale.indexOf(fromColorName)
        return colorScale[colorIndex + steps]
      },
      stepTo (fromColor, to) {
        /**
         * Returns color on the same scale, shifted to a specific step
         * @fromColor color name (or alias) to shift from
         * @to positive or negative integer
         */
        if (fromColor == null) return null
        if (to == null) return fromColor
        const fromColorName = this.getName(fromColor)?.replace(/[0-9]/g, '')
        return fromColorName + to
      },
    }

    // Make every color individually available
    for (const color in colors) {
      Vue.prototype['$color'][color] = colors[color]
    }
  },
}
